import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface AnnadanamContent {
  title: string;
  subtitle: string;
  festivalOnlyNote: string;
  mainHeading: string;
  mainDescription: string;
  significance1Title: string;
  significance1Description: string;
  significance2Title: string;
  significance2Description: string;
  significance3Title: string;
  significance3Description: string;
  festivalMealsTitle: string;
  festivalMealsDescription: string;
  mealsPerDay: string;
  mealsCount: string;
  averageCost: string;
  festivalDays: string;
  donationLevel1Name: string;
  donationLevel1Amount: string;
  donationLevel1Description: string;
  donationLevel2Name: string;
  donationLevel2Amount: string;
  donationLevel2Description: string;
  donationLevel3Name: string;
  donationLevel3Amount: string;
  donationLevel3Description: string;
  improvementTitle: string;
  improvementNote: string;
  contactNote: string;
}

export default function AnnadanamManager() {
  const [content, setContent] = useState<AnnadanamContent>({
    title: 'Annadanam',
    subtitle: 'Sacred Food Offering During Ulsav Festival',
    festivalOnlyNote: '🎉 Annadanam is exclusively offered during our annual Ulsav (Festival) celebration. Not available year-round.',
    mainHeading: 'Annadanam - The Divine Gift of Nourishment',
    mainDescription: 'Annadanam is the sacred practice of offering free meals to devotees during Ulsav, our most significant temple festival. This ancient tradition embodies the temple\'s commitment to community welfare and spiritual nourishment. During the festival period, we serve hundreds of devotees and visitors with nutritious meals prepared with devotion and care.',
    significance1Title: 'Spiritual Significance',
    significance1Description: 'In Hindu tradition, offering food to devotees is considered "Annadanam Maha Danam" (the greatest of all donations). It directly nourishes both body and spirit during sacred celebrations.',
    significance2Title: 'Community Bond',
    significance2Description: 'Annadanam during Ulsav brings the entire community together, fostering unity and shared spiritual experience. It strengthens our bonds as a temple family.',
    significance3Title: 'Festival Essence',
    significance3Description: 'The festival atmosphere is incomplete without the sacred sharing of meals. Your support makes celebrations more meaningful for every devotee who visits.',
    festivalMealsTitle: 'Ulsav Festival Annadanam Details',
    festivalMealsDescription: 'During the Ulsav festival period, our temple serves meals to all devotees who visit. The preparation and service require significant resources and dedicated effort from our community.',
    mealsPerDay: '400-500 meals served daily during festival',
    mealsCount: 'Approximately 2000-3000 meals during festival period',
    averageCost: '₹40-50 per meal (including ingredients, preparation, service)',
    festivalDays: 'Festival duration: 5-7 days (dates vary by lunar calendar)',
    donationLevel1Name: 'Day Sponsor',
    donationLevel1Amount: '₹1500',
    donationLevel1Description: 'Sponsor meals for one complete day of the festival. Help us feed 400-500 devotees with a nutritious meal.',
    donationLevel2Name: 'Festival Supporter',
    donationLevel2Amount: '₹5000',
    donationLevel2Description: 'Support the entire festival Annadanam program. Your contribution makes a significant difference in serving our community.',
    donationLevel3Name: 'Festival Benefactor',
    donationLevel3Amount: '₹10000',
    donationLevel3Description: 'Become a primary supporter of Ulsav Annadanam. Your generosity enables comprehensive meal service throughout the festival.',
    improvementTitle: '💡 Your Contribution Improves Our Service',
    improvementNote: 'Donations help us enhance food quality, accommodate dietary preferences, and improve service efficiency during the festival.',
    contactNote: '📞 For specific festival dates and donation coordination, please contact the temple office. Donations can be made through this page or directly at the temple.'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, []);

  const loadContent = () => {
    try {
      console.log('🔄 AnnadanamManager: Setting up real-time listener...');
      const docRef = doc(db, 'annadanam_content', 'main');
      setLoading(true);
      
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        try {
          if (docSnap.exists()) {
            const data = docSnap.data() as AnnadanamContent;
            setContent(data);
            console.log('✅ Admin: Real-time content loaded from Firebase');
          } else {
            console.warn('⚠️ Admin: Document does not exist, using defaults');
          }
        } catch (error) {
          console.error('Error processing content:', error);
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error('❌ Admin Error in onSnapshot:', error);
        setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
        setLoading(false);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up listener:', error);
      setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
      setLoading(false);
      return () => {};
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('💾 Saving annadanam content to Firebase...');
      const docRef = doc(db, 'annadanam_content', 'main');
      await setDoc(docRef, content);
      console.log('✅ Content saved successfully to annadanam_content/main');
      setSnackbar({ open: true, message: 'Content saved successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error saving content:', error);
      setSnackbar({ open: true, message: 'Error saving content', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof AnnadanamContent, value: string) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Annadanam Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage the annadanam donation page content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          📄 Main Content
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Title" value={content.title} onChange={(e) => handleChange('title', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Subtitle" value={content.subtitle} onChange={(e) => handleChange('subtitle', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Festival Only Note" value={content.festivalOnlyNote} onChange={(e) => handleChange('festivalOnlyNote', e.target.value)} multiline rows={2} sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Main Heading" value={content.mainHeading} onChange={(e) => handleChange('mainHeading', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Main Description" value={content.mainDescription} onChange={(e) => handleChange('mainDescription', e.target.value)} multiline rows={4} sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          ✨ Significance Sections
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} key={`sig${i}`}>
              <TextField fullWidth label={`Significance ${i} Title`} value={content[`significance${i}Title` as keyof AnnadanamContent]} onChange={(e) => handleChange(`significance${i}Title` as keyof AnnadanamContent, e.target.value)} size="small" sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
              <TextField fullWidth label={`Significance ${i} Description`} value={content[`significance${i}Description` as keyof AnnadanamContent]} onChange={(e) => handleChange(`significance${i}Description` as keyof AnnadanamContent, e.target.value)} multiline rows={2} sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🍽️ Festival Meals Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Section Title" value={content.festivalMealsTitle} onChange={(e) => handleChange('festivalMealsTitle', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Section Description" value={content.festivalMealsDescription} onChange={(e) => handleChange('festivalMealsDescription', e.target.value)} multiline rows={3} sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Meals Per Day" value={content.mealsPerDay} onChange={(e) => handleChange('mealsPerDay', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Meals Count" value={content.mealsCount} onChange={(e) => handleChange('mealsCount', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Average Cost" value={content.averageCost} onChange={(e) => handleChange('averageCost', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Festival Days" value={content.festivalDays} onChange={(e) => handleChange('festivalDays', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🎁 Donation Levels
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} key={`level${i}`}>
              <TextField fullWidth label={`Donation Level ${i} Name`} value={content[`donationLevel${i}Name` as keyof AnnadanamContent]} onChange={(e) => handleChange(`donationLevel${i}Name` as keyof AnnadanamContent, e.target.value)} size="small" sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
              <TextField fullWidth label={`Donation Level ${i} Amount`} value={content[`donationLevel${i}Amount` as keyof AnnadanamContent]} onChange={(e) => handleChange(`donationLevel${i}Amount` as keyof AnnadanamContent, e.target.value)} size="small" sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
              <TextField fullWidth label={`Donation Level ${i} Description`} value={content[`donationLevel${i}Description` as keyof AnnadanamContent]} onChange={(e) => handleChange(`donationLevel${i}Description` as keyof AnnadanamContent, e.target.value)} multiline rows={2} sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          ℹ️ Additional Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Improvement Title" value={content.improvementTitle} onChange={(e) => handleChange('improvementTitle', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Improvement Note" value={content.improvementNote} onChange={(e) => handleChange('improvementNote', e.target.value)} multiline rows={2} sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Contact Note" value={content.contactNote} onChange={(e) => handleChange('contactNote', e.target.value)} multiline rows={2} sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
        </Grid>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity as 'success' | 'error'} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
